import { z } from 'zod'

/**
 * Turning "the event I'm looking at" into the id our importer needs.
 *
 * Social Tables events are identified by a uid. Prismm events are identified by
 * a numeric occasion id, and Social Tables only knows them as
 * `prismm_occasion_id` on a wrapping ST event. Asking a user for either id is
 * asking them to find something the product barely labels, so this accepts what
 * they *can* copy: the address bar.
 *
 *   https://home.socialtables.com/events/3337f7a6-681b-4dfb-b4de-1ce1e3f313d9
 *   https://ops.prismm.com/occasion/3315442/seating
 *
 * Two inputs are rejected with their own message rather than a generic one,
 * because both look plausible and neither works:
 *
 * - **A bare number.** Could be a Social Tables `legacy_id` (the API no longer
 *   accepts it) or a Prismm occasion. Paste the URL so we can tell.
 * - **A diagram link** (`diagram.socialtables.com/diagram/<uid>`). That uid is a
 *   diagram's, not an event's; passing it silently imports nothing.
 */

/** Any 8-4-4-4-12 hex id. Deliberately version-agnostic: live events use v1 (`…-11f1-…`) and v4. */
const UID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
/** Job-config encoding for a Prismm occasion, so it cannot be confused with a uid. */
const PRISMM_PREFIX = 'prismm:'

export type PrismEventRefOk =
  | { readonly ok: true; readonly kind: 'socialtables'; readonly eventId: string }
  | { readonly ok: true; readonly kind: 'prismm'; readonly occasionId: number }

export type PrismEventRef = PrismEventRefOk | { readonly ok: false; readonly reason: string }

/** A recognized paste, ready to show in the form and send as `eventIds`. */
export type PrismEventRefHit = {
  kind: 'socialtables' | 'prismm'
  /** Value stored on the job: a Social Tables uid, or `prismm:<occasionId>`. */
  encoded: string
  /** Short line for the preview, e.g. "Social Tables · 3337f7a6-…". */
  label: string
}

/** True for a bare Social Tables event uid. */
export function isPrismEventUid(value: string): boolean {
  return UID.test(value.trim())
}

/** Encode a recognized ref the way the import job stores `eventIds`. */
export function encodePrismEventRef(ref: PrismEventRefOk): string {
  return ref.kind === 'socialtables' ? ref.eventId : `${PRISMM_PREFIX}${String(ref.occasionId)}`
}

/**
 * Read a stored `eventIds` entry back. Uids pass through; `prismm:3315442`
 * becomes an occasion id the runner looks up in Social Tables' list.
 */
export function decodePrismEventRef(
  encoded: string,
): { kind: 'socialtables'; eventId: string } | { kind: 'prismm'; occasionId: number } | null {
  const value = encoded.trim()
  if (isPrismEventUid(value)) return { kind: 'socialtables', eventId: value.toLowerCase() }
  if (value.toLowerCase().startsWith(PRISMM_PREFIX)) {
    const n = Number(value.slice(PRISMM_PREFIX.length))
    if (Number.isInteger(n) && n > 0) return { kind: 'prismm', occasionId: n }
  }
  return null
}

function hitFrom(ref: PrismEventRefOk): PrismEventRefHit {
  if (ref.kind === 'socialtables') {
    return {
      kind: 'socialtables',
      encoded: encodePrismEventRef(ref),
      label: `Social Tables · ${ref.eventId}`,
    }
  }
  return {
    kind: 'prismm',
    encoded: encodePrismEventRef(ref),
    label: `Prismm · occasion ${String(ref.occasionId)}`,
  }
}

/**
 * Read one event out of whatever a user pasted: a Social Tables uid or event
 * URL, or a Prismm occasion URL (ops.prismm.com / the SAML hop).
 */
export function parsePrismEventRef(raw: string): PrismEventRef {
  const input = raw.trim().replace(/^["'<]+|["'>]+$/g, '')
  if (input === '') {
    return { ok: false, reason: 'Empty.' }
  }
  if (isPrismEventUid(input)) {
    return { ok: true, kind: 'socialtables', eventId: input.toLowerCase() }
  }
  const tagged = decodePrismEventRef(input)
  if (tagged?.kind === 'prismm') {
    return { ok: true, kind: 'prismm', occasionId: tagged.occasionId }
  }
  if (/^\d+$/.test(input)) {
    return {
      ok: false,
      reason: `“${input}” is a number, not a link. A Social Tables URL ends in a uid; a Prismm seating URL looks like https://ops.prismm.com/occasion/${input}/seating. Paste the address bar.`,
    }
  }

  const looksLikeUrl = input.includes('://') || /^[\w-]+(\.[\w-]+)+\//.test(input)
  if (!looksLikeUrl) {
    return { ok: false, reason: `“${truncate(input)}” is not a Social Tables or Prismm event link.` }
  }

  const url = safeUrl(input)
  if (!url) {
    return { ok: false, reason: `“${truncate(input)}” is not a link we can read.` }
  }
  const segments = url.pathname.split('/').filter(Boolean)
  const host = url.hostname.replace(/^www\./, '')

  // A diagram link carries a diagram's uid. It parses, it just is not the event.
  if (segments.includes('diagram') || segments.includes('diagrams')) {
    return {
      ok: false,
      reason: 'That is a diagram link, not an event. Open the event and paste the address bar.',
    }
  }

  const occasion = occasionIdFromPath(segments, host)
  if (occasion != null) {
    return { ok: true, kind: 'prismm', occasionId: occasion }
  }

  // Last uid, not first: reversed rather than `findLast`, which is newer than
  // this package's TypeScript target.
  const uid = [...segments].reverse().find((segment) => UID.test(segment))
  if (uid !== undefined) {
    return { ok: true, kind: 'socialtables', eventId: uid.toLowerCase() }
  }
  return { ok: false, reason: `That link has no event id in it: ${truncate(url.pathname)}` }
}

/**
 * Prismm occasion id from a seating / SAML path. Host-gated so a Social Tables
 * `/events/15156204` (the old numeric id) is not mistaken for an occasion.
 */
function occasionIdFromPath(segments: string[], host: string): number | null {
  const prismmHost =
    host === 'ops.prismm.com' ||
    host === 'prismm.com' ||
    host.endsWith('.prismm.com') ||
    host === 'allseated.com' ||
    host.endsWith('.allseated.com') ||
    host === 'login.socialtables.com'
  if (!prismmHost) return null
  for (let i = 0; i < segments.length - 1; i++) {
    const key = segments[i]
    const next = segments[i + 1]
    if ((key === 'occasion' || key === 'ops') && next && /^\d+$/.test(next)) {
      const n = Number(next)
      return n > 0 ? n : null
    }
  }
  return null
}

/**
 * Parse a whole textarea: one reference per line, or comma-separated. Returns the
 * ids in the order given, deduplicated, plus a problem per line that failed, so
 * a caller can show all of them at once instead of only the first.
 */
export function parsePrismEventRefs(raw: string): {
  eventIds: string[]
  items: PrismEventRefHit[]
  problems: Array<{ input: string; reason: string }>
} {
  const eventIds: string[] = []
  const items: PrismEventRefHit[] = []
  const problems: Array<{ input: string; reason: string }> = []
  const seen = new Set<string>()
  for (const entry of raw.split(/[\s,]+/).filter((s) => s.trim() !== '')) {
    const parsed = parsePrismEventRef(entry)
    if (!parsed.ok) {
      problems.push({ input: entry, reason: parsed.reason })
      continue
    }
    const next = hitFrom(parsed)
    if (!seen.has(next.encoded)) {
      seen.add(next.encoded)
      eventIds.push(next.encoded)
      items.push(next)
    }
  }
  return { eventIds, items, problems }
}

/** One reference as request input: a uid, a `prismm:` occasion, or a pasted URL. */
export const prismEventRefSchema = z
  .string()
  .min(1)
  .max(2_000)
  .transform((raw, ctx) => {
    const parsed = parsePrismEventRef(raw)
    if (!parsed.ok) {
      ctx.addIssue({ code: 'custom', message: parsed.reason })
      return z.NEVER
    }
    return encodePrismEventRef(parsed)
  })

function safeUrl(input: string): URL | null {
  try {
    return new URL(input.includes('://') ? input : `https://${input}`)
  } catch {
    return null
  }
}

function truncate(value: string): string {
  return value.length > 60 ? `${value.slice(0, 57)}…` : value
}
